import {
  ApolloClient,
  ApolloError,
  ApolloLink,
  InMemoryCache,
  createHttpLink,
  Observable,
} from '@apollo/client/core'
import type { DocumentNode } from 'graphql'
import { onError } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'
import { showToast } from '@/utils/showToast'
import { sendError, sendInfo } from '@/utils/sendError'
import { isOffline, onReconnect } from '@/utils/networkStatus'
import { isConnected } from '@/utils/connectivity'
import { isAppInForeground } from '@/utils/appState'
import { isBackgroundOperation } from '@/utils/backgroundOperation'
import { useLocalizationStore, type TranslationKey } from '@/stores/localization'
import { serverUnavailable, markServerUnavailable } from './serverAvailability'

export { serverUnavailable }

function getStatusCode(error: Error): number | null {
  return 'statusCode' in error ? (error as { statusCode: number }).statusCode : null
}

// A transport failure with no HTTP status (the fetch itself never reached the
// server) — i.e. a lost/absent connection, as opposed to a server or GraphQL
// error. Callers use this to treat a lost connection as an expected outcome.
export function isConnectivityError(error: unknown): boolean {
  if (!(error instanceof ApolloError)) return false
  const { networkError } = error
  return networkError != null && getStatusCode(networkError) === null
}

// The server is unreachable or erroring: a 5xx, or — while the device itself is
// online — a transport failure that never reached it. Distinct from an offline
// device (handled as normal connectivity loss) and from a GraphQL/business error
// (a real rejection the caller must not retry). Callers use it to fall back to the
// offline replica (entitled) and to keep pending writes queued instead of dropping
// them.
export function isServerDownError(error: unknown): boolean {
  if (error instanceof ApolloError) {
    const { networkError } = error
    if (networkError == null) return false
    const statusCode = getStatusCode(networkError)
    if (statusCode != null) return statusCode >= 500
    // A transport failure with no status: the server is down only if the device
    // itself is online (otherwise it's ordinary offline connectivity loss).
    return !isOffline()
  }
  // A non-Apollo error only counts when it carries a 5xx HTTP status; a plain
  // error is an app/unknown failure, not an outage.
  if (error instanceof Error) {
    const statusCode = getStatusCode(error)
    return statusCode != null && statusCode >= 500
  }
  return false
}

function getNetworkErrorKey(error: Error): TranslationKey {
  const statusCode = getStatusCode(error)
  if (statusCode === null) {
    return 'errorNoInternet'
  }
  if (statusCode >= 500) {
    return 'errorServerUnavailable'
  }
  return 'errorRequestFailed'
}

function showNetworkErrorToast(error: Error): void {
  const { t } = useLocalizationStore()
  void showToast({ text: t(getNetworkErrorKey(error)), duration: 'long' })
}

// Detect a server outage and raise the shared flag (which starts the recovery
// poller and shows the "please wait" overlay for users without an offline
// catalogue). The error is passed straight through — reads then fall back to the
// replica for entitled users, and writes stay queued — rather than blocking the
// operation here.
const serverUnavailableLink = new ApolloLink((operation, forward) => {
  return new Observable((observer) => {
    const sub = forward(operation).subscribe({
      next: (result) => observer.next(result),
      complete: () => observer.complete(),
      error: (error) => {
        if (isServerDownError(error)) {
          sendInfo('retry: server unavailable trigger', {
            source: 'apollo-serverUnavailableLink',
            operation: operation.operationName,
            statusCode: String(getStatusCode(error) ?? 'none'),
            errorMessage: error instanceof Error ? error.message : String(error),
          })
          markServerUnavailable()
        }
        observer.error(error)
      },
    })
    return () => sub.unsubscribe()
  })
})

let offlineToastShown = false
onReconnect(() => {
  offlineToastShown = false
})

const errorLink = onError(({ networkError, graphQLErrors, operation }) => {
  if (networkError) {
    const isUserInitiated = !isBackgroundOperation(operation.getContext())
    // While offline this is an expected connectivity failure: don't report it,
    // and show the toast only once per offline period so parallel failing
    // operations don't spam identical toasts.
    if (isOffline()) {
      if (!offlineToastShown && isUserInitiated) {
        offlineToastShown = true
        showNetworkErrorToast(networkError)
      }
    } else {
      sendError(networkError, { operation: operation.operationName })
      if (isUserInitiated) showNetworkErrorToast(networkError)
    }
  }
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      sendError(err, { operation: operation.operationName })
    }
  }
})

const retryLink = new RetryLink({
  delay: { initial: 300, max: 3000, jitter: true },
  attempts: {
    max: 3,
    retryIf: (error, operation) => {
      if (!error || error.statusCode === 400) return false
      // Not connected (offline / forced-offline / server down): stop retrying and
      // let the read serve offline data. The recovery poller restores access.
      if (!isConnected()) return false
      if (!isAppInForeground()) return false
      const definition = operation.query.definitions[0]
      if (definition.kind === 'OperationDefinition' && definition.operation === 'mutation') {
        return false
      }
      sendInfo('retry: apollo retryLink attempt', {
        source: 'apollo-retryLink',
        operation: operation.operationName,
        statusCode: String(error.statusCode ?? 'none'),
        errorName: error instanceof Error ? error.name : 'unknown',
        errorMessage: error instanceof Error ? error.message : String(error),
      })
      return true
    },
  },
})

const httpLink = createHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_API_URL,
})

const cache = new InMemoryCache({
  typePolicies: {
    NoveltyCollectionVolumeInfo: { merge: true },
  },
})

const apolloClient = new ApolloClient({
  link: errorLink.concat(serverUnavailableLink).concat(retryLink).concat(httpLink),
  cache,
})

export { apolloClient }
export type { DocumentNode }
